import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { Button } from "../../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { config } from "../../../../Environment";
import useRetryFetch from "../../../../hooks/useRetryFetch";
import useProcess from "../../../../hooks/useProcess";
import { Edit } from "lucide-react";
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../../components/ui/pagination";
import LoggerContext from "../../../../context/LoggerProvider";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../../../hooks/useAuth";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { EXPERT_ROLE } from "../../../../lib/roles";
import {
  EngagementTaggingDialog,
  type EngagementTaggingResult,
} from "../../../runActivity/modals/EngagementTaggingDialog";

const itemsPerPage = 4;

const UnfinishedEngagementModal = ({
  unfinishedEngagementModal,
  setUnfinishedEngagementModal,
  prescription,
  setPrescriptions,
  refreshLists,
}: any) => {
  const { auth, user } = useAuth();
  const isCurrentUserExpert = Array.isArray(user?.roles) && user.roles.includes(EXPERT_ROLE);

  const [editingEngagement, setEditingEngagement] = useState<
    | {
        id: string;
        name: string;
      }
    | any
  >({
    id: "",
    name: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [newEngagementName, setNewEngagementName] = useState("");
  const [showTaggingDialog, setShowTaggingDialog] = useState(false);
  const totalPages = useMemo(() => {
    const runningProcesses =
      prescription?.processes.filter((p: any) => p.status === "RUNNING") || [];
    return Math.ceil(runningProcesses.length / itemsPerPage);
  }, [prescription?.processes, itemsPerPage]);

  const { Logger } = useContext(LoggerContext);

  const retryFetch = useRetryFetch();
  const { startProcess, resumeProcess } = useProcess();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const currentEngagements = useMemo(() => {
    const runningProcesses =
      prescription?.processes.filter((p: any) => p.status === "RUNNING") || [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    return runningProcesses.slice(startIndex, startIndex + itemsPerPage);
  }, [currentPage, prescription?.processes, itemsPerPage]);

  const handleUpdateProcessName = async (
    processId: string,
    newName: string
  ) => {
    if (newName.trim() === "") return;

    try {
      const updateResponse = await retryFetch(
        `${config.API_BASE}/api/process/${processId}/engagement-name`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            engagementName: newName,
          }),
        }
      );

      const data = await updateResponse.json();

      if (!updateResponse.ok) {
        throw new Error(data?.message || "Failed to update process name");
      }

      setPrescriptions((prevPrescriptions: any) =>
        prevPrescriptions.map((prescription: any) => {
          if (prescription._id === data.process.fiPrescription) {
            return {
              ...prescription,
              processes: prescription.processes.map((process: any) =>
                process._id === processId
                  ? { ...process, engagementName: newName }
                  : process
              ),
            };
          }
          return prescription;
        })
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : String(error ?? "Unknown error");
      toast.error(message);
      console.log("Failed to update process name:", error);
    } finally {
      setEditingEngagement(null);
    }
  };

  const getDefaultEngagementName = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const timeStr = now.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
    });
    return `${prescription?.name || "Engagement"} - ${dateStr} ${timeStr}`;
  };

  const handleStartNew = () => {
    if (isCurrentUserExpert) {
      setShowTaggingDialog(true);
    } else {
      const prescriptionId = prescription._id;
      const engagementName = newEngagementName.trim() || getDefaultEngagementName();
      startProcess({
        prescriptionId,
        engagementName,
        onError: () => {
          toast.error("An error occurred while starting the prescription.");
        },
      });
    }
  };

  const handleTaggingConfirm = async (result: EngagementTaggingResult) => {
    setShowTaggingDialog(false);
    const prescriptionId = prescription._id;
    const engagementName = result.engagementName?.trim() || newEngagementName.trim() || getDefaultEngagementName();
    await startProcess({
      prescriptionId,
      engagementName,
      tags: result.tags,
      clientIdentifier: result.clientIdentifier,
      onError: () => {
        toast.error("An error occurred while starting the prescription.");
      },
    });
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const range: (number | "...")[] = [];
    const delta = 1;

    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    range.push(1);

    if (left > 2) range.push("...");

    for (let i = left; i <= right; i++) {
      range.push(i);
    }

    if (right < totalPages - 1) range.push("...");

    if (totalPages > 1) range.push(totalPages);

    return range;
  };

  const pageNumbers = useMemo(getPageNumbers, [currentPage, totalPages]);

  useEffect(() => {
    setCurrentPage(1);
    if (unfinishedEngagementModal) {
      setNewEngagementName(getDefaultEngagementName());
    }
  }, [unfinishedEngagementModal]);

  const continueProcess = async (process: any) => {
    let processToContinue = process._id;
    if (process.pendingInnerProcess) {
      Logger.debug("PENDING INNER PROCESS detected, switching to this one!");
      processToContinue = processToContinue.pendingInnerProcess;
    }
    await resumeProcess({
      processId: processToContinue,
      onError: () => {
        toast.error(
          "An error occurred while continuing the process. Please try again."
        );
        refreshLists();
      },
    });
  };

  return (
    <>
    <Dialog
      open={unfinishedEngagementModal}
      onOpenChange={setUnfinishedEngagementModal}
    >
      <DialogContent className="max-w-2xl h-fit max-h-[90vh] md:h-[34rem] gap-y-2 px-2 md:px-6">
        <DialogHeader>
          <DialogTitle>
            {t("unfinished_engagement_history_dialog_title")}
          </DialogTitle>
          <DialogDescription className="line-clamp-1">
            {t("unfinished_engagement_history_dialog_description", {
              name: prescription?.name,
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto pr-2">
          <div className="max-w-2xl">
            {/* New Engagement Card - Now First */}
            <div className="bg-gradient-to-r from-primary/5 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-primary/20 dark:border-primary rounded-lg p-3 md:p-4 mt-2">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-9 h-9 bg-primary rounded-full shadow">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                      {t("new_engagement")}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {t("start_from_scratch")}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col md:flex-row gap-2">
                  <Input
                    id="engagement-name"
                    value={newEngagementName}
                    onChange={(e) => setNewEngagementName(e.target.value)}
                    placeholder={t("enter_engagement_name") || "Enter engagement name"}
                    className="flex-1 h-9 text-sm"
                  />
                  <Button
                    onClick={() => handleStartNew()}
                    size="sm"
                    className="text-white shadow hover:shadow-md transition-all h-9 px-4 whitespace-nowrap"
                  >
                    <svg
                      className="w-3.5 h-3.5 mr-1.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    {t("start_new")}
                  </Button>
                </div>
              </div>
            </div>

            {/* Compact OR Divider */}
            <div className="flex items-center my-3">
              <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
              <div className="px-3">
                <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-primary to-primary rounded-full shadow">
                  <span className="text-white font-bold text-xs select-none">
                    {t("or")}
                  </span>
                </div>
              </div>
              <div className="flex-1 border-t border-gray-200 dark:border-gray-700"></div>
            </div>

            {/* Existing Engagements Table */}
            <div className="border rounded-md overflow-hidden">
              <Table>
                {/* Header with sticky positioning */}
                <TableHeader className="sticky top-0 z-10 bg-background">
                  <TableRow>
                    <TableHead className="min-w-[120px] md:min-w-[200px] w-full">
                      {t("engagement_name")}
                    </TableHead>
                    <TableHead className="w-auto text-right p-1 hidden md:table-cell">
                      {t("actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>

                {/* Body with vertical scroll */}
                <TableBody className="overflow-y-auto">
                  {currentEngagements?.map((process: any) => (
                    <TableRow key={process._id} className="h-11 max-h-11">
                      {/* Engagement Name + Edit */}
                      <TableCell className="border-none px-2 py-0 w-full align-middle max-w-0">
                        {editingEngagement?.id === process._id ? (
                          <div className="flex items-center gap-2 h-8">
                            <Input
                              value={editingEngagement.name}
                              onChange={(e) =>
                                setEditingEngagement({
                                  ...editingEngagement,
                                  name: e.target.value,
                                })
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleUpdateProcessName(
                                    process._id,
                                    editingEngagement.name
                                  );
                                } else if (e.key === "Escape") {
                                  setEditingEngagement(null);
                                }
                              }}
                              className="h-8 text-sm w-full"
                              autoFocus
                            />
                            <Button
                              size="sm"
                              className="h-8 px-2 py-0"
                              onClick={() =>
                                handleUpdateProcessName(
                                  process._id,
                                  editingEngagement.name
                                )
                              }
                            >
                              {t("save")}
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 h-8 min-w-0">
                            <div className="truncate whitespace-nowrap overflow-hidden flex-1 text-sm min-w-0">
                              {process.engagementName}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 flex-shrink-0"
                              onClick={() =>
                                setEditingEngagement({
                                  id: process._id,
                                  name: process.engagementName,
                                })
                              }
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>

                      {/* Resume Button - Own Column */}
                      <TableCell className="border-none px-2 py-0 w-[1%] whitespace-nowrap align-middle">
                        <Button
                          size="sm"
                          className="h-7 text-xs px-3"
                          onClick={() => continueProcess(process)}
                        >
                          {t("resume")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {/* Footer with pagination - sticky at bottom */}
                {prescription?.processes?.length > 0 && (
                  <TableFooter className="sticky bottom-0 z-10 bg-background border-t">
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-right px-0 py-2 border-none"
                      >
                        <Pagination>
                          <PaginationContent className="justify-center space-x-1">
                            {/* Previous */}
                            <PaginationItem>
                              <PaginationPrevious
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(currentPage - 1);
                                }}
                                aria-disabled={currentPage === 1}
                              />
                            </PaginationItem>

                            {/* Page Numbers (Only on md and above) */}
                            <div className="hidden md:flex">
                              {pageNumbers.map((page, idx) =>
                                page === "..." ? (
                                  <PaginationItem key={`ellipsis-${idx}`}>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                ) : (
                                  <PaginationItem key={page}>
                                    <PaginationLink
                                      href="#"
                                      isActive={currentPage === page}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handlePageChange(page);
                                      }}
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                )
                              )}
                            </div>

                            {/* Next */}
                            <PaginationItem>
                              <PaginationNext
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handlePageChange(currentPage + 1);
                                }}
                                aria-disabled={currentPage === totalPages}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                )}
              </Table>
            </div>

            {prescription?.processes?.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400">
                  {t("engagementHistoryDialogEmptyMessage")}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2 h-fit mt-3">
            <Button
              variant="outline"
              onClick={() => setUnfinishedEngagementModal(false)}
            >
              {t("cancel")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
    <EngagementTaggingDialog
      isOpen={showTaggingDialog}
      onConfirm={handleTaggingConfirm}
      onClose={() => setShowTaggingDialog(false)}
      activityName={prescription?.name}
      confirmLabel={t("start_activity") || "Start Activity"}
      showEngagementNameField={true}
      initialEngagementName={newEngagementName}
      showMentorOptions={isCurrentUserExpert}
    />
    </>
  );
};

export default UnfinishedEngagementModal;
